import Head from "next/head";
import React, { useState, useEffect } from "react";
import LoginTemplate from "@/templates/LoginTemplate";
import { userSession, userLogin, adminLoginRedirect , getUserById} from "services/user.service";
import {
  getAuth,
  getCompanyId,
  setAuth,
  setCompanyId,
  setCompanyTimeZone,
} from "services/identity.service";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import "yup-phone";
import { yupValidator } from "lib/yup-validator";
import {
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input/input";
import en from "react-phone-number-input/locale/en.json";
import { getPlatformUserRole, isCustomerSuccess, roleDefaultPage } from "lib/utils";
import APIResponseToast, { useToast } from "molecules/APIResponseToast";
import { useDispatch } from "react-redux";
import { setCompanies, setSelectedCompany } from "@/redux/company-users";
import { getCompanyAndRolesByUserIdCompanyId } from "services/company.service";
import { PRIVILEGE_SLUG, ROLES } from "constants";
import {
  buildPrivilegesMapFromRoles,
  hasPrivilege,
  normalizePrivileges,
} from "services/permission.service";
import { ensurePostHogReady, trackUserLogin } from "lib/analytics/posthog-helper";
const LoginIndex = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { toast, showToastByResponse } = useToast();
  const yupValidator = (schema) =>
    async (data, context) => {
      try {
        const values = { ...data, loginType: context.parent.loginType };
        await schema.validate(values, { abortEarly: false });
        return { isValid: true };
      } catch (errors) {
        return { isValid: false, errors };
      }
    };

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupValidator(yup.object().shape(
      {
        mobileNumber: yup.string().when('loginType', {
          is: "mobileNumber",
          then: yup.string().phone(null, null, "Please enter a valid phone number").required('A phone number is required'),
          otherwise: yup.string().nullable().notRequired()
        }),
        loginType: yup.string(),
        emailId: yup.string().when('loginType', {
          is: "email",
          then: yup.string().email("Please enter a valid e-mail").max(255).required("Email is required"),
          otherwise: yup.string().nullable().notRequired()
        }),
      }
    )),defaultValues:{
      loginType: "mobileNumber"
    }
  });
  const countryOptionsList = [];
  getCountries().forEach((country) => {
    countryOptionsList.push({
      value: country,
      label: en[country] + " +" + getCountryCallingCode(country),
      countryCode: getCountryCallingCode(country),
    });
  });
  const [country, setCountry] = useState({
    value: "IN",
    label: "+91",
    countryCode: 91,
  });
  const handleCountryChange = (country) => {
    const val = "+" + getCountryCallingCode(country.value);
    setCountry({
      value: country.val,
      label: val,
      countryCode: parseInt(getCountryCallingCode(country.value)),
    });
  };

  const [apiError, setApiError] = useState([]);
  const [apiErrorStatus, setApiErrorStatus] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [loginDetails, setLoginDetails] = useState({
    step: 1,
    mobileNumber: "",
    countryCode: 91,
    emailId: "",
    loginType: "",
  });

  const onClickBackArrow = () => {
    setLoginDetails({
      ...loginDetails,
      step: 0
    })
  }

  const handleLoginOptionSelect = (type) => {
    setValue('loginType', type)
    setLoginDetails({
      ...loginDetails,
      step: 1
    })
  }
  const handleGetOTP = async (data) => {
    const { loginType, mobileNumber, emailId } = getValues();
    const res = await userLogin(mobileNumber, country.countryCode, loginType, emailId ? emailId.toLowerCase() : emailId);
    if (res.status) {
      setLoginDetails({
        step: 2,
        mobileNumber: mobileNumber,
        emailId: emailId ? emailId.toLowerCase() : emailId,
        loginType: loginType,
        countryCode: country.countryCode,
      });
      setApiError([]);
    } else {
      setApiError(res.message);
      setApiErrorStatus(res?.statusCode);
    }
  };

  const handleLogin = async (otp) => {
    if (otp.length == 4) {
      const res = await userSession(
        loginDetails.mobileNumber,
        loginDetails.emailId,
        loginDetails.loginType,
        otp,
        loginDetails.countryCode
      );
      if (res.status) {
        const sessionRoles = res.entity?.roles || [];
        const platformUserRole = getPlatformUserRole(sessionRoles);
        const hasPlatformSuperAdmin = platformUserRole === ROLES.SUPER_ADMIN;
        const hasPlatformCustomerSuccess = isCustomerSuccess(sessionRoles);
        const privileges = normalizePrivileges(
          res?.entity?.privileges || buildPrivilegesMapFromRoles(sessionRoles)
        );
        const userUsingSuperAdminHome =
          hasPlatformSuperAdmin ||
          hasPlatformCustomerSuccess ||
          hasPrivilege(privileges, PRIVILEGE_SLUG.organizationManagement) ||
          hasPrivilege(privileges, PRIVILEGE_SLUG.organizationJourneyManagement) ||
          hasPrivilege(privileges, PRIVILEGE_SLUG.allUsers) ||
          hasPrivilege(privileges, PRIVILEGE_SLUG.companies);
        setAuth({
          id: res.entity.id,
          token: res.entity.token,
          userUsingSuperAdminHome,
        });
        
        // Track user login
        const userData = { type: "admin", user: res?.entity };
        if (ensurePostHogReady(userData)) {
          trackUserLogin({
            userId: res?.entity?.id,
            userRole: platformUserRole || res?.entity?.roles?.[0]?.name || "unknown",
            loginMethod: loginDetails?.loginType,
          });
        }
        
        if (userUsingSuperAdminHome) {
          setCompanyId(null);
          router.push("/superadmin");
        } else if (res.entity?.company.length == 1) {
          setCompanyId(res.entity?.company[0].id);
          setCompanyTimeZone(res.entity?.company[0].timeZone);
          const company = res.entity.company[0];
          const routerPathObj = roleDefaultPage(res.entity?.roles, company);
          router.replace(routerPathObj);

        } else {
          dispatch(setCompanies({ companies: res?.entity?.company }));
          setLoginDetails({
            ...loginDetails,
            step: 3,
          });
        }
      } else {
        setApiError(res.message);
        setApiErrorStatus(res?.statusCode);
      }
    }
  };

  const resendOtp = async () => {
    const { loginType, emailId } = getValues();
    const res = await userLogin(loginDetails.mobileNumber, loginDetails.countryCode, loginType, emailId);
    
    if (!res.status) {
      setApiError(res.message);
      setApiErrorStatus(res.statusCode);
    }
  };

  const editMobile = () => {
    setApiError([]);
    setLoginDetails((prevState) => {
      return { ...prevState, step: 1 };
    });
  };

  const onCompanySelect = async (company) => {
    const auth = getAuth();
    if (auth?.userUsingSuperAdminHome) {
      setCompanyId(null);
      router.replace("/superadmin");
      return;
    }
    setCompanyId(company.id);
    setCompanyTimeZone(company.timeZone);
    const res = await getCompanyAndRolesByUserIdCompanyId(
      auth?.id,
      company?.id
    );
    if (res?.status) {
      if (res?.entity?.token) {
        setAuth({ ...auth, token: res?.entity?.token });
      }
      dispatch(setSelectedCompany({ selectedCompany: res?.entity }));
      const currentCompany = res?.entity
      const roles = res.entity?.userRoles.map(item => item?.Role)
      const routerPathObj = roleDefaultPage(roles, currentCompany)
      router.replace(routerPathObj);
    } else {
      setApiError(res?.message);
      setApiErrorStatus(res?.statusCode);
    }
  };

useEffect(() => {
  const checkAndRedirect = async () => {
    const auth = getAuth();
    if (auth) {
      setIsAuthenticated(true);
      await adminLoginRedirect(router);
      return;
    }
  };
  checkAndRedirect();
}, []);


  if (isAuthenticated) {
    return <></>;
  }

  return (
    <>
      <Head>
        <title>Login</title>
      </Head>
      <APIResponseToast toast={toast} />
      <LoginTemplate
        // login
        handleSubmit={handleSubmit}
        control={control}
        register={register}
        country={country}
        handleCountryChange={handleCountryChange}
        countryOptionsList={countryOptionsList}
        setError={setApiError}
        handleLoginOptionSelect={handleLoginOptionSelect}
        getValues={getValues}
        setValue={setValue}
        isAdmin={true}
        //
        loginDetails={loginDetails}
        apiError={apiError}
        apiErrorStatus={apiErrorStatus}
        handleGetOTP={handleGetOTP}
        handleLogin={handleLogin}
        resendOtp={resendOtp}
        editMobile={editMobile}
        onCompanySelect={onCompanySelect}
        onClickBackArrow={onClickBackArrow}
      />
    </>
  );
};
export default LoginIndex;